// El contexto de venta de un crédito, leído por las pantallas de crédito. Son DOS lecturas del silo
// que fallan por separado: `status` cubre el equipo y `storeNameStatus` cubre la tienda.
import type { LoanCreditSaleContext, LoanSaleContextReason } from '@/api/services/loan-credit';

/** El detalle que agrega el silo sobre por qué la venta no se resolvió. */
export const SALE_CONTEXT_REASON_NOTE: Record<LoanSaleContextReason, string> = {
    CREDIT_WITHOUT_WIZARD_SESSION: 'El crédito nunca quedó vinculado a una sesión del wizard.',
    WIZARD_LOOKUP_FAILED: 'La consulta al silo del wizard falló.',
    BORROWER_LOOKUP_FAILED: 'La consulta al acreditado del crédito falló.',
    BORROWER_NOT_FOUND: 'El acreditado del crédito no existe en el silo de crédito.',
    STORE_ROSTER_FAILED: 'No se pudo leer el padrón de tiendas del comercio.',
    STORE_NOT_IN_ROSTER: 'La tienda de la venta no figura en el padrón del comercio.',
    RETAILER_UNKNOWN: 'El comercio de la venta no se pudo identificar.',
    CUSTOMER_ANCHOR_MISSING: 'La venta no registró a qué cliente de retail corresponde.',
    CUSTOMER_NAME_LOOKUP_FAILED: 'La consulta al expediente de retail falló.',
    CUSTOMER_NOT_FOUND: 'El cliente de la venta no existe en el expediente de retail.',
    CUSTOMER_WITHOUT_NAME: 'El cliente existe en retail, pero no tiene nombre registrado.',
};

/** Pega el detalle del silo atrás del motivo, cuando lo manda. */
function withReason(note: string, reason: LoanSaleContextReason | undefined): string {
    return reason === undefined ? note : `${note} ${SALE_CONTEXT_REASON_NOTE[reason]}`;
}

/** Descarta la cadena en blanco, que no es un nombre. */
const named = (value: string | null): value is string => value !== null && value.trim() !== '';

/**
 * El MODELO del equipo financiado, o `undefined` si la venta no lo resolvió. Cae a la marca solo
 * cuando no hay modelo: la celda mide 112px y «Samsung Galaxy A25 5G» se trunca donde el modelo cabe.
 */
export function saleProductLabel(sale: LoanCreditSaleContext): string | undefined {
    if (sale.status !== 'OK') return undefined;
    if (named(sale.productModel)) return sale.productModel;
    const label = [sale.productBrand, sale.productModel].filter(named).join(' ');
    return label === '' ? undefined : label;
}

/**
 * La MARCA cruda del equipo, para el combo de filtro. No es lo que se pinta: la celda muestra el
 * modelo (`saleProductLabel`) y el filtro agrupa por marca, como el mock.
 */
export function saleProductBrand(sale: LoanCreditSaleContext): string | undefined {
    if (sale.status !== 'OK' || !named(sale.productBrand)) return undefined;
    return sale.productBrand;
}

/** Por qué la celda de Producto quedó sin valor. Solo se lee cuando no hay etiqueta que pintar. */
export function saleProductNote(sale: LoanCreditSaleContext): string {
    switch (sale.status) {
        case 'NOT_FOUND':
            return withReason(
                'Se buscó la venta que originó este crédito y no existe: nadie registró qué equipo se financió.',
                sale.reason,
            );
        case 'UNAVAILABLE':
            return withReason(
                'La venta que originó este crédito no se pudo leer, así que no sabemos qué equipo se financió.',
                sale.reason,
            );
        case 'INVALID_ID':
            return withReason(
                'La referencia de este crédito a su venta está mal formada: reintentar no la resuelve.',
                sale.reason,
            );
        default:
            return 'La venta que originó este crédito se leyó completa y no registró marca ni modelo del equipo.';
    }
}

/**
 * Nombre de la tienda donde se vendió; sin nombre resuelto cae al `storeId` crudo, como el mock.
 * La tienda no depende de `status`: el crédito persiste su propio `storeId` desde 2026-08.
 */
export function saleStoreName(sale: LoanCreditSaleContext): string | undefined {
    if (sale.storeNameStatus === 'OK' && named(sale.storeName)) return sale.storeName;
    return named(sale.storeId) ? sale.storeId : undefined;
}

/** Si `saleStoreName` devolvió un nombre del padrón o el id crudo de la tienda. */
export function saleStoreNameIsResolved(sale: LoanCreditSaleContext): boolean {
    return sale.storeNameStatus === 'OK' && named(sale.storeName);
}

/** Por qué la celda de Tienda quedó sin valor. Solo se lee cuando no hay nombre que pintar. */
export function saleStoreNote(sale: LoanCreditSaleContext): string {
    switch (sale.storeNameStatus) {
        case 'NOT_FOUND':
            return sale.storeId === null
                ? withReason(
                      'La venta que originó este crédito no registró en qué tienda se hizo.',
                      sale.storeNameReason,
                  )
                : withReason(
                      'La tienda de esta venta no se pudo traducir a un nombre.',
                      sale.storeNameReason,
                  );
        case 'UNAVAILABLE':
            return withReason(
                'El nombre de la tienda no se pudo leer. No es que la tienda no exista: la lectura falló.',
                sale.storeNameReason,
            );
        case 'INVALID_ID':
            return withReason(
                'La referencia de este crédito a su venta está mal formada: reintentar no la resuelve.',
                sale.storeNameReason,
            );
        default:
            return 'El padrón de tiendas respondió sin nombre para la tienda de esta venta.';
    }
}

/* ================================================================================
   CLIENTE — la TERCERA lectura del silo. Falla aparte del equipo y de la tienda.
   ================================================================================ */

/** Si `saleCustomerName` devolvió un nombre de verdad o el id crudo del cliente de retail. */
export function saleCustomerNameIsResolved(sale: LoanCreditSaleContext): boolean {
    return sale.customerNameStatus === 'OK' && named(sale.customerName);
}

/**
 * Nombre del acreditado; sin nombre resuelto cae al `retailCustomerId` crudo, como cae la tienda a
 * su id. No depende de `status`: el nombre se resuelve APARTE del equipo.
 */
export function saleCustomerName(sale: LoanCreditSaleContext): string | undefined {
    const name = sale.customerName;
    if (sale.customerNameStatus === 'OK' && named(name)) return name;
    return named(sale.retailCustomerId) ? sale.retailCustomerId : undefined;
}

/** La lectura del nombre del cliente falló; no significa que el cliente no exista. */
export const UNREADABLE_CUSTOMER_NOTE =
    'El nombre del cliente no se pudo leer. No es que el cliente no exista: la lectura falló.';

/** Por qué la celda de Cliente no tiene nombre. Solo se lee cuando no hay nombre que pintar. */
export function saleCustomerNote(sale: LoanCreditSaleContext): string {
    switch (sale.customerNameStatus) {
        case 'NOT_FOUND':
            return sale.retailCustomerId === null
                ? withReason(
                      'La venta que originó este crédito no registró a qué cliente se le vendió.',
                      sale.customerNameReason,
                  )
                : withReason(
                      'El cliente de esta venta no se pudo traducir a un nombre.',
                      sale.customerNameReason,
                  );
        case 'UNAVAILABLE':
            return withReason(UNREADABLE_CUSTOMER_NOTE, sale.customerNameReason);
        case 'INVALID_ID':
            return withReason(
                'La referencia de este crédito a su venta está mal formada: reintentar no la resuelve.',
                sale.customerNameReason,
            );
        default:
            return 'El expediente de retail respondió sin nombre para el cliente de esta venta.';
    }
}

/** Marca y modelo del equipo financiado; `undefined` si la venta no registró ninguno. */
export function saleProductNameOf(sale: LoanCreditSaleContext): string | undefined {
    const name = [sale.productBrand, sale.productModel].filter(named).join(' ');
    return name === '' ? undefined : name;
}
